import ReactDOM from "react-dom/client";
import "./index.css";
import "./App.css";
import { StrictMode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen.ts";
import { AuthProvider } from "./components/authProvider.tsx";
import * as Sentry from "@sentry/react";
import { ErrorFallbackComponent } from "./components/errorFallback.tsx";

const router = createRouter({ routeTree, disableGlobalCatchBoundary: true });

Sentry.init({
  dsn: "https://62e90c27cdd5554e564f5f39d762c93d@o4509713531076608.ingest.de.sentry.io/4510335613665360",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.tanstackRouterBrowserTracingIntegration(router),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
  // Enable logs to be sent to Sentry
  enableLogs: true,
});

const queryClient = new QueryClient();

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <AuthProvider>
        <Sentry.ErrorBoundary
          fallback={(errorData) => (
            <ErrorFallbackComponent
              error={errorData.error}
              resetError={errorData.resetError}
              router={router}
            />
          )}
        >
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </Sentry.ErrorBoundary>
      </AuthProvider>
    </StrictMode>
  );
}
